import { Button } from 'components/ui'
import UsersList from 'components/CommonComponents/UsersList'
import { Box, Flex, Tooltip } from '@chakra-ui/core'
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import useLocaleText from 'components/useLocaleText'
import { Controller, useFormContext } from 'react-hook-form'
import { FormInputLabel } from 'components/TasksV2/UI/FormInputLabel'
import { AuditorsModal } from '../AuditorsModal'
import { AuditorsAvatars } from '../AuditorsAvatars'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { Text } from '@chakra-ui/core'
import { ErrorMessage } from 'components/ui/Input/ErrorMessage'
import { WidgetWrapper } from 'components/TasksV2/UI/WidgetWrapper'
import { UserPopover } from 'components/ui/UserPopover'
import { MdLocalPolice } from 'react-icons/md'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'

const VM_FEATURE = 'VM'

const areSameIds = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false
  }

  const rightIds = new Set(right)

  return left.every((id) => rightIds.has(id))
}

type AudienceValue = {
  groups?: string[]
  users?: string[]
  tags?: string[]
}

const getMandatoryAuditorsByAudience = ({
  audience,
  groupsObject,
  retailTags,
  mappedGroupIdsByUsers,
}: {
  audience: AudienceValue
  groupsObject: RootState['config']['config']['groups']
  retailTags: RootState['config']['config']['tags']
  mappedGroupIdsByUsers: RootState['config']['config']['mappedGroupIdsByUsers']
}) => {
  const audienceGroupIds = new Set(audience.groups ?? [])
  const audienceUserIds = new Set(audience.users ?? [])
  const selectedTagIds = new Set(audience.tags ?? [])

  retailTags?.forEach((tag) => {
    if (!selectedTagIds.has(tag.sk)) {
      return
    }

    tag.groups?.forEach((groupId) => audienceGroupIds.add(groupId))
    tag.users?.forEach((uid) => audienceUserIds.add(uid))
  })

  audienceUserIds.forEach((uid) => {
    mappedGroupIdsByUsers?.[uid]?.forEach((groupId) => audienceGroupIds.add(groupId))
  })

  const auditorIds = new Set<string>()

  audienceGroupIds.forEach((groupId) => {
    groupsObject?.[groupId]?.auth_users_by_ft?.[VM_FEATURE]?.forEach((uid) => auditorIds.add(uid))
  })

  return Array.from(auditorIds)
}

interface AuditorsContentProps {
  inline?: boolean
  listHeight?: number
  onChange?: (auditorIds: string[]) => void
}

const AuditorsContent: React.FC<AuditorsContentProps> = ({ onChange = () => {}, inline = false, listHeight = 400 }) => {
  const theme = useSelector((state: RootState) => state.general.theme)
  const { errors, watch, setValue, triggerValidation } = useFormContext()
  const { isVmTask } = useFormStepContext()
  const {
    groups: groupsObject,
    tags: retailTags,
    mappedGroupIdsByUsers,
  } = useSelector((state: RootState) => state.config.config)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const audience = (watch('audience') || {}) as AudienceValue
  const currentAuditors = (watch('auditors') || []) as string[]

  const autoManagedAuditorIdsRef = useRef<string[]>([])

  const mandatoryAuditorIds = useMemo(
    () =>
      isVmTask
        ? getMandatoryAuditorsByAudience({
            audience,
            groupsObject,
            retailTags,
            mappedGroupIdsByUsers,
          })
        : [],
    [audience, groupsObject, isVmTask, mappedGroupIdsByUsers, retailTags]
  )

  const t_update_auditors = useLocaleText('update_auditors') || 'Update Auditors'
  const t_auditors = useLocaleText('auditors') || 'Auditors'
  const auditorsTooltip = useLocaleText('auditors_tooltip') || 'Auditors are the users who will be assigned to the task'

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleSubmit = useCallback(
    (selectedAuditors: string[]) => {
      onChange(Array.from(new Set([...selectedAuditors, ...mandatoryAuditorIds])))
    },
    [mandatoryAuditorIds, onChange]
  )

  useEffect(() => {
    const autoManagedAuditorIdsSet = new Set(autoManagedAuditorIdsRef.current)
    const mandatoryAuditorIdsSet = new Set(mandatoryAuditorIds)

    const auditorsWithoutStaleMandatory = currentAuditors.filter(
      (uid) => !autoManagedAuditorIdsSet.has(uid) || mandatoryAuditorIdsSet.has(uid)
    )
    const auditorsBeforeAutoAddSet = new Set(auditorsWithoutStaleMandatory)
    const autoAddedAuditorIds = mandatoryAuditorIds.filter((uid) => !auditorsBeforeAutoAddSet.has(uid))
    const nextAutoManagedAuditorIds = mandatoryAuditorIds.filter(
      (uid) => autoManagedAuditorIdsSet.has(uid) || autoAddedAuditorIds.includes(uid)
    )
    const nextAuditors = Array.from(new Set([...auditorsWithoutStaleMandatory, ...autoAddedAuditorIds]))

    autoManagedAuditorIdsRef.current = nextAutoManagedAuditorIds

    if (areSameIds(nextAuditors, currentAuditors)) {
      return
    }

    setValue('auditors', nextAuditors)
    triggerValidation('auditors')
  }, [currentAuditors, mandatoryAuditorIds, setValue, triggerValidation])

  if (inline)
    return (
      <UsersList
        initialSelectedIds={currentAuditors}
        lockedSelectedIds={mandatoryAuditorIds}
        onSelectionChange={handleSubmit}
        listHeight={listHeight}
      />
    )

  return (
    <Box>
      <Tooltip label={auditorsTooltip} aria-label={auditorsTooltip} hasArrow placement="bottom" zIndex={10000}>
        <Flex alignItems="center" style={{ columnGap: '10px' }}>
          <WidgetWrapper
            flexDir="row"
            alignItems="center"
            justifyContent="space-between"
            isError={!!errors?.auditors}
            p="10px"
            w="100%"
          >
            <Text>{t_auditors}</Text>
            {currentAuditors.length > 0 && (
              <UserPopover userIds={currentAuditors}>
                <AuditorsAvatars auditorIds={currentAuditors} maxVisible={5} />
              </UserPopover>
            )}
            <Button
              type="button"
              size="sm"
              onClick={handleOpenModal}
              bg={theme?.elementsColor}
              color="white"
              w="max-content"
            >
              <Text>{t_update_auditors}</Text>
            </Button>
          </WidgetWrapper>
          <Flex alignItems="center" justifyContent="center" bg="#ababab" p="6px" borderRadius="full">
            <MdLocalPolice size={18} color="white" />
          </Flex>
        </Flex>
      </Tooltip>
      {errors?.auditors && (
        <Box px="10px" pt="5px">
          <ErrorMessage errorMessage={errors.auditors.message} />
        </Box>
      )}
      <AuditorsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        initialAuditors={currentAuditors}
        lockedAuditorIds={mandatoryAuditorIds}
        onSubmit={handleSubmit}
      />
    </Box>
  )
}

const ChooseAuditors = ({ inline = false, listHeight = 400 }: { inline?: boolean; listHeight?: number }) => {
  const t_update_auditors = useLocaleText('update_auditors') || 'Update Auditors'
  const t_auditors_required = useLocaleText('auditors_required') || 'Please select at least one auditor'

  const { control } = useFormContext()

  return (
    <Box>
      {!inline && <FormInputLabel label={t_update_auditors} isRequired />}

      <Controller
        name="auditors"
        control={control}
        rules={{
          validate: (value) => {
            return (value && value.length > 0) || t_auditors_required
          },
        }}
        as={<AuditorsContent inline={inline} listHeight={listHeight} />}
      />
    </Box>
  )
}

export default ChooseAuditors
